import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, switchMap, take } from 'rxjs';

import { Issue } from '@ninety/platform/models/issues/issue';
import { Headline } from '@ninety/platform/models/meetings/headline';
import { Milestone } from '@ninety/platform/models/rocks/milestone';
import { Rock } from '@ninety/platform/models/rocks/rock';
import { Todo } from '@ninety/platform/models/todos/todo';
import { selectIssuesUrl, selectMilestonesUrl, selectRocksUrl } from '@ninety/platform/state';
import { environment } from '@ninety/ui/web/environments';

import {
  InboxAlertDTO,
  InboxAlertListResponse,
  InboxAlertView,
  InboxConversationDTO,
  InboxConversationListResponse,
  InboxConversationMessageDTO,
  InboxConversationMessageListResponse,
  InboxCounts,
} from '../models/inbox-api.model';

/** `companyId` and the acting user come from the auth context server-side, never from here.
 *
 * The inbox is served by `ninety-notifications`, so its routes sit under `/v1` on a separate origin.
 * `NotificationsInterceptor` attaches the bearer token by origin prefix. Don't route these through
 * `HostInterceptor` — it only rewrites `/api/v4` and adds cookies this service doesn't use. */
@Injectable({ providedIn: 'root' })
export class InboxService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly api = `${environment.notificationsApiOrigin}/v1/inbox`;

  /*
   * The calls below go to the monolith, not the inbox API — one per item type the reading pane can
   * card. A row carries its item's title and team but not the owner, date, or description, and the
   * card wants all three. Rocks, milestones and issues resolve their host through the store because
   * which service answers is flag-gated per operation.
   */

  getTodo(todoId: string): Observable<Todo> {
    return this.http.get<Todo>(`/api/v4/Todos/${todoId}`);
  }

  getRock(rockId: string): Observable<Rock> {
    return this.byId<Rock>(this.store.select(selectRocksUrl('getById')), rockId);
  }

  getMilestone(milestoneId: string): Observable<Milestone> {
    return this.byId<Milestone>(this.store.select(selectMilestonesUrl('getById')), milestoneId);
  }

  getIssue(issueId: string): Observable<Issue> {
    return this.byId<Issue>(this.store.select(selectIssuesUrl('get')), issueId);
  }

  /** Cascading messages are headlines with a flag, and answer on the same route. */
  getHeadline(headlineId: string): Observable<Headline> {
    return this.http.get<Headline>(`/api/v4/Headlines/${headlineId}`);
  }

  private byId<T>(baseUrl$: Observable<string>, id: string): Observable<T> {
    return baseUrl$.pipe(
      take(1),
      switchMap(url => this.http.get<T>(`${url}/${id}`))
    );
  }

  /** `view` is required; nothing in the UI reaches the archive, so it's always active. 50 is the
   * server's max, not a preference — a bigger `limit` is a 400, not a clamp. */
  getAlerts(page = 1, limit = 50): Observable<InboxAlertListResponse> {
    const params = new HttpParams().set('view', InboxAlertView.active).set('page', page).set('limit', limit);
    return this.http.get<InboxAlertListResponse>(`${this.api}/alerts`, { params });
  }

  /** The badge's only polling target. Counted with the same visibility rules the lists use. */
  getCounts(): Observable<InboxCounts> {
    return this.http.get<InboxCounts>(`${this.api}/counts`);
  }

  markAlertRead(alertId: string): Observable<InboxAlertDTO> {
    return this.http.patch<InboxAlertDTO>(`${this.api}/alerts/${alertId}/read`, {});
  }

  resolveAlert(alertId: string): Observable<InboxAlertDTO> {
    return this.http.patch<InboxAlertDTO>(`${this.api}/alerts/${alertId}/resolve`, {});
  }

  unresolveAlert(alertId: string): Observable<InboxAlertDTO> {
    return this.http.patch<InboxAlertDTO>(`${this.api}/alerts/${alertId}/unresolve`, {});
  }

  getConversations(page = 1, limit = 50): Observable<InboxConversationListResponse> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.http.get<InboxConversationListResponse>(`${this.api}/conversations`, { params });
  }

  /** Idempotent on the server via a unique `pairKey` — a double-click can't create two threads. */
  createConversation(recipientUserId: string): Observable<InboxConversationDTO> {
    return this.http.post<InboxConversationDTO>(`${this.api}/conversations`, { recipientUserId });
  }

  getConversationMessages(
    conversationId: string,
    page = 1,
    limit = 50
  ): Observable<InboxConversationMessageListResponse> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.http.get<InboxConversationMessageListResponse>(`${this.api}/conversations/${conversationId}/messages`, {
      params,
    });
  }

  sendConversationMessage(conversationId: string, body: string): Observable<InboxConversationMessageDTO> {
    return this.http.post<InboxConversationMessageDTO>(`${this.api}/conversations/${conversationId}/messages`, {
      body,
    });
  }

  markConversationRead(conversationId: string): Observable<{ success: true }> {
    return this.http.patch<{ success: true }>(`${this.api}/conversations/${conversationId}/read`, {});
  }
}
